import React, { useRef } from 'react';

const PREFS = [
  '東京都', '大阪府', '愛知県', '北海道', '福岡県', 'その他'
];
const REPORT_TYPES = [
  { value: 'daily', label: '日報' },
  { value: 'monthly', label: '月報' },
  { value: 'other', label: 'その他' },
];

export type SupporterFormValues = {
  email: string;
  password?: string;
  passwordConfirm?: string;
  name: string;
  displayName: string;
  company: string;
  post1: string;
  post2: string;
  pref: string;
  city: string;
  address1: string;
  address2: string;
  tel: string;
  mobile: string;
  logo: File | null;
  reportTypes: string[];
  estimateEdit: string; // 'yes' or 'no'
};

type FormProps = {
  form: SupporterFormValues;
  setForm: React.Dispatch<React.SetStateAction<SupporterFormValues>>;
  showPassword?: boolean;
  setShowPassword?: React.Dispatch<React.SetStateAction<boolean>>;
  showPasswordConfirm?: boolean;
  setShowPasswordConfirm?: React.Dispatch<React.SetStateAction<boolean>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleLogoClear: () => void;
  logoInputRef?: React.RefObject<HTMLInputElement>;
  showPasswordFields?: boolean;
};

const Form: React.FC<FormProps> = ({
  form,
  setForm,
  showPassword,
  setShowPassword,
  showPasswordConfirm,
  setShowPasswordConfirm,
  handleChange,
  handleLogoClear,
  logoInputRef,
  showPasswordFields = false,
}) => {
  // logoInputRefが未指定の場合は内部でrefを生成
  const internalLogoInputRef = useRef<HTMLInputElement>(null);
  const logoRef = logoInputRef || internalLogoInputRef;

  return (
    <form className="space-y-5">
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">メールアドレス</label>
        <input type="email" name="email" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.email} onChange={handleChange} />
      </div>
      {showPasswordFields && (
        <>
          <div>
            <label className="block mb-1 text-sm font-semibold text-gray-500">パスワード</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="w-full px-2 py-1 pr-10 border border-gray-200 rounded"
                value={form.password || ''}
                onChange={e => setForm((prev: SupporterFormValues) => ({ ...prev, password: e.target.value }))}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600"
                tabIndex={-1}
                onClick={() => setShowPassword && setShowPassword(v => !v)}
                aria-label={showPassword ? '非表示' : '表示'}
              >
                {showPassword ? (
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
                ) : (
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><path d="M4 4l12 12" stroke="currentColor" strokeWidth="1.5"/></svg>
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-semibold text-gray-500">パスワード確認</label>
            <div className="relative">
              <input
                type={showPasswordConfirm ? 'text' : 'password'}
                name="passwordConfirm"
                className="w-full px-2 py-1 pr-10 border border-gray-200 rounded"
                value={form.passwordConfirm || ''}
                onChange={e => setForm((prev: SupporterFormValues) => ({ ...prev, passwordConfirm: e.target.value }))}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600"
                tabIndex={-1}
                onClick={() => setShowPasswordConfirm && setShowPasswordConfirm(v => !v)}
                aria-label={showPasswordConfirm ? '非表示' : '表示'}
              >
                {showPasswordConfirm ? (
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
                ) : (
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><path d="M4 4l12 12" stroke="currentColor" strokeWidth="1.5"/></svg>
                )}
              </button>
            </div>
          </div>
        </>
      )}
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">名前</label>
        <input type="text" name="name" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.name} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">表示名</label>
        <input type="text" name="displayName" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.displayName} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">会社名</label>
        <input type="text" name="company" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.company} onChange={handleChange} />
      </div>
      <div className="flex gap-2">
        <div className="w-1/2">
          <label className="block mb-1 text-sm font-semibold text-gray-500">郵便番号（上3桁）</label>
          <input type="text" name="post1" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.post1} onChange={handleChange} maxLength={3} />
        </div>
        <div className="w-1/2">
          <label className="block mb-1 text-sm font-semibold text-gray-500">郵便番号（下4桁）</label>
          <input type="text" name="post2" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.post2} onChange={handleChange} maxLength={4} />
        </div>
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">都道府県</label>
        <select name="pref" className="w-full px-2 py-1 text-sm font-semibold text-gray-500 border border-gray-200 rounded" value={form.pref} onChange={handleChange}>
          <option value="">選択してください</option>
          {PREFS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">市区町村</label>
        <input type="text" name="city" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.city} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">住所１</label>
        <input type="text" name="address1" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.address1} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">住所２</label>
        <input type="text" name="address2" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.address2} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">電話番号 <span className="text-xs text-gray-400">（固定電話がない場合は、携帯電話番号を入力してください）</span></label>
        <input type="text" name="tel" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.tel} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">携帯電話番号</label>
        <input type="text" name="mobile" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.mobile} onChange={handleChange} />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">会社ロゴ</label>
        <input
          type="file"
          name="logo"
          accept="image/*"
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          onChange={handleChange}
          ref={logoRef}
        />
        {form.logo && (
          <div className="relative flex items-center gap-4 mt-2">
            <div className="relative inline-block">
              <img src={URL.createObjectURL(form.logo)} alt="会社ロゴプレビュー" className="object-contain h-16 border rounded" />
              <button
                type="button"
                className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-0.5 shadow hover:bg-gray-100"
                style={{ lineHeight: 1 }}
                onClick={handleLogoClear}
                aria-label="画像をキャンセル"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="9" cy="9" r="9" fill="#fff"/>
                  <path d="M6 6l6 6M12 6l-6 6" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">レポート種類</label>
        <div className="flex flex-wrap gap-4 mt-1">
          {REPORT_TYPES.map(r => (
            <label key={r.value} className="flex items-center gap-1 text-sm font-semibold text-gray-500">
              <input
                type="checkbox"
                name="reportTypes"
                value={r.value}
                checked={form.reportTypes.includes(r.value)}
                onChange={handleChange}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">見積もり編集</label>
        <div className="flex gap-6 pl-1 mt-1">
          <label className="flex items-center gap-1 text-sm font-semibold text-gray-500">
            <input type="radio" name="estimateEdit" value="yes" checked={form.estimateEdit === 'yes'} onChange={handleChange} />
            <span>可</span>
          </label>
          <label className="flex items-center gap-1 text-sm font-semibold text-gray-500">
            <input type="radio" name="estimateEdit" value="no" checked={form.estimateEdit === 'no'} onChange={handleChange} />
            <span>不可</span>
          </label>
        </div>
      </div>
    </form>
  );
};

export default Form;
